import Backbone from 'backbone';
import $ from 'jquery';
import _ from 'underscore';
import TemplateHtml from '../../../html/arcade.html';
import Alerts from '../../alerts/alerts';
import Analytics from '../../analytics';
import BaseView from '../../baseView';
import Environment from '../../environment';
import HwpTvDispatcher from '../../hwpTvDispatcher';
import InfoButton from '../../infoButton';
import ArcadeSkins from '../../manager/arcadeSkinManager';
import AudioManager from '../../manager/audioManager';
import DeepLinkManager from '../../manager/deepLinkManager';
import AppAuthManager from '../../manager/hwptvAppAuthManager';
import PopUpManager from '../../manager/popUpManager';
import Verizon from '../../platform/verizon';
import Router from '../../router';
import SpriteFactory from '../../sprites/spriteFactory';
import FontLoader from '../../util/fontLoader';
import PerformanceStats from '../../util/performanceStats/performanceStats';
import { addReadyPromise, arrayPromiseChain } from '../../util/utilities';
import { leaderboardAdventToNodeGames } from '../advent/adventLeaderboardUtils';
import { EVENTS } from '../baseGame.types';
import TeamChooser from '../teamChooser/teamChooser';
import { ARCADE_EVENTS, ATTRIBUTES, HUB_EVENTS } from './arcade.types';
import ArcadeModel from './arcadeModel';
import Header from './header';

/**
 * @typedef {import('../../util/utilities').IReadyPromise} IReadyPromise
 * @typedef {import('../../baseView').BaseViewOptions} BaseViewOptions
 * @typedef {import('../baseGame.types').BaseGameOptions} BaseGameOptions
 * @typedef {import('./arcade.types').ArcadeOptions} ArcadeOptions
 * @typedef {import('./arcade.types').PageOptions} PageOptions
 * @typedef {import('../baseGame.types').GenericButtonOptions} GenericButtonOptions
 *
 * @typedef {BaseViewOptions & ArcadeOptions} ArcadeViewOptions
 */

/** @type {Arcade | undefined} */
let instance;

/**
 * @class
 * @extends {BaseView<ArcadeViewOptions>}
 * @implements {IReadyPromise}
 */
export default class Arcade extends BaseView {
	static get instance() {
		return instance;
	}

	get CN() {
		return Arcade.name;
	}

	get template() {
		return _.template(TemplateHtml);
	}

	id() {
		return 'arcade';
	}

	/**
	 * @param {ArcadeViewOptions} options
	 */
	constructor(options) {
		if (instance) {
			throw new Error('Singleton already exists');
		}

		super(options);
		instance = this;
		this.logger.log('[constructor]', { options });

		const { isReady, onReadyReject, onReadyResolve } = addReadyPromise();

		/** @type {Promise<Arcade>} */
		this.isReady = isReady;
		/** @type {Function} */
		this.onReadyResolve = onReadyResolve;
		/** @type {Function} */
		this.onReadyReject = onReadyReject;

		/** @type {ArcadeModel} */
		this.arcadeState = new ArcadeModel(options).on(
			ARCADE_EVENTS.initialized,
			this.onDataLoaded.bind(this),
		);
	}

	/**
	 * @param {ArcadeViewOptions} options
	 */
	initialize(options) {
		this.logger.log(`[${this.initialize.name}]`, { options });

		super.initialize(options);
		this.configureForEnvironment();

		this.render();
	}

	configureForEnvironment() {
		this.logger.log(`[${this.configureForEnvironment.name}]`);

		// Configure tv mode
		if (Environment.isSetTopBox() || Environment.isAndroidTv()) {
			this.addArcadeSkinId('tv');
		}

		if (!Environment.isProd()) {
			const stats = new PerformanceStats();
			requestAnimationFrame(function updateStats() {
				stats.update();
				requestAnimationFrame(updateStats);
			});
			$('body').append(stats.dom);
		}
	}

	onDataLoaded() {
		this.logger.log(`[${this.onDataLoaded.name}]`);

		const {
			arcadeName = '',
			accountVariant,
			cognitoCheckDisabled,
			audioManager,
			helpPopUp,
			sprites,
			popUpManager,
			deepLinkManager,
		} = this.arcadeState.attributes;

		// Set Analytics prefix so auth analytics works correctly
		Analytics.setArcade(arcadeName);

		if (audioManager) {
			AudioManager.initialize(audioManager);
		}
		if (helpPopUp) {
			InfoButton.initialize(helpPopUp);
		}
		if (sprites) {
			SpriteFactory.initialize(sprites);
		}
		if (popUpManager) {
			PopUpManager.initialize(popUpManager);
		}
		if (deepLinkManager) {
			DeepLinkManager.initialize(deepLinkManager);
		}

		return Promise.all([
			HwpTvDispatcher.isReady.finally(),
			Analytics.isReady.finally(),
			DeepLinkManager.isReady.finally(),
		])
			.then(() => AppAuthManager.initialize({ accountVariant, cognitoCheckDisabled }))
			.then(() => this.addSkin())
			.then(() => this.entitlementCheck())
			.then(() => FontLoader.preloadDocumentFonts())
			.then(() => this.addHeader())
			.then(() => this.processLeaderboards())
			.then(() => this.registerRoutes())
			.then(() => this.insertInDom())
			.then(() => {
				this.logger.info(`[Initialization Complete]`);
				return Analytics.sendEvent('ArcadeLaunch', { engine: 'Arcade' });
			})
			.then(() => this.trigger(ARCADE_EVENTS.initialized))
			.then(
				() => this.onReadyResolve(this),
				(/** @type {Error} */ error) => {
					this.logger.error(error);
					this.onReadyReject(error);
				},
			);
	}

	addSkin() {
		this.logger.log(`[${this.addSkin.name}]`);

		const { skin: className, skinModules: skins } = this.arcadeState.attributes;
		this.logger.debug({ className, skins });

		if (className) {
			this.addArcadeSkinId(className);
		}

		return Promise.resolve()
			.then(() => {
				// Check for any skins to be loaded
				if (skins) {
					return Promise.all(
						skins
							.split(/[,\s]+/) // Split ot comma, space, or both to separate names of multiple skins
							.map((skin = '') => skin.trim())
							.map((skin = '') => ArcadeSkins.loadSkin(skin)),
					).then(() => {}); // Trailing promise to fix typing issues.
				}

				return Promise.resolve();
			})
			.then(() => {
				/** @type {BaseGameOptions | undefined} */
				const teamChooserPage = this.arcadeState
					.get('pages')
					?.find(({ type }) => type === 'teamChooser');

				if (teamChooserPage?.gameId) {
					// Apply any Team Chooser skins that have been chosen
					return new Promise((resolve) => {
						const teamChooser = new TeamChooser(teamChooserPage);
						teamChooser.once(EVENTS.initialized, () => {
							if (teamChooser.chosenTeam) {
								this.logger.log({ chosenTeam: teamChooser.chosenTeam });
								this.addArcadeSkinId(teamChooser.chosenTeam);
							}
							resolve(undefined);
						});
					});
				}

				return Promise.resolve();
			});
	}

	/**
	 * Centralized function to add a classname that alters the visuals of the arcade.
	 * @param {string} skinClassName The classname to add.
	 */
	addArcadeSkinId(skinClassName) {
		this.logger.log(`[${this.addArcadeSkinId.name}]`, { skinClassName });

		$('body').addClass(skinClassName);
	}

	addHeader() {
		this.logger.log(`[${this.addHeader.name}]`);

		return new Promise((resolve) => {
			const { disableHeader, headerImage, pages } = this.arcadeState.attributes;
			if (!disableHeader) {
				this.header = new Header({ pages, headerImage });
				// Header is added after body to facilitate ~ css selector for styling specific pages
				this.$el.append(this.header.el);
			}
			resolve(undefined);
		});
	}

	/**
	 * Checks all leaderboards pages for nested games such as Advent and replaces them with their
	 * 'nested' games if neccesary.
	 * @returns {Promise<any>}
	 */
	processLeaderboards() {
		this.logger.log(`[${this.processLeaderboards.name}]`);

		const { pages = [] } = this.arcadeState.attributes;
		const leaderboardPages = pages.filter(({ type }) => type === 'leaderboards');

		return arrayPromiseChain(leaderboardPages, leaderboardAdventToNodeGames);
	}

	registerRoutes() {
		this.logger.log(`[${this.registerRoutes.name}]`);

		return new Promise((resolve) => {
			const { pages = [] } = this.arcadeState.attributes;

			if (!pages.length) {
				this.logger.warn(
					'Arcade data has no pages, ensure data column is using "pages.id".',
				);
			}

			pages.forEach((page, index) => {
				const { route, id, engine } = { ...page };

				if (route) {
					const pageRoute = index > 0 ? route : `*${route}`;
					this.logger.debug('[Registering Page]', `[${pageRoute}]`, page);
					Router.registerRoute(pageRoute, id, engine, page);
				} else {
					this.logger.debug(`[No Route provided for page entry]`, { page });
				}
			});

			Router.on('pageMounted', this.onPageMounted.bind(this));
			Router.on('route', this.onRoute.bind(this));

			resolve(undefined);
		});
	}

	entitlementCheck() {
		this.logger.log(`[${this.entitlementCheck.name}]`);

		const entitlementEntity = this.arcadeState.get('entitlementEntity');
		if (entitlementEntity) {
			if (Environment.isVerizonSetTopBox()) {
				Verizon.isSubscriber(entitlementEntity).then(() =>
					this.addArcadeSkinId('subscriber'),
				);
			} else {
				this.logger.info(
					`Platform does not support entitlment check for "${entitlementEntity}`,
				);
			}
		}
	}

	insertInDom() {
		this.logger.log(`[${this.insertInDom.name}]`);

		const { body, referrer } = document;
		this.dismissElement(body)
			.then(() => {
				body.classList.remove('splashed');
				$(body).append(Alerts.el, InfoButton.el, this.el);

				Backbone.history.start();
			})
			.then(() => this.displayElement(body))
			.then(() => {
				if (window.parent && referrer) {
					// Allows any arcade to be loaded within the hub
					window.parent.postMessage(HUB_EVENTS.finishedLoading, referrer);
				}
			});
	}

	/**
	 * Handles the route change event in the Arcade application.
	 * Updates the background of the page based on the current route.
	 *
	 * @param {string} pageId - The ID of the current page.
	 * @returns {void}
	 */
	onRoute(pageId) {
		this.logger.log(`[${this.onRoute.name}]`, { pageId });

		const { arcadeBg, bgAudio: arcadeBgAudio, pages } = this.arcadeState.attributes;
		const pageData = pages?.find(({ id }) => id === pageId);
		const { pageBg = '', bgAudio: pageBgAudio } = pageData || {};

		if (!pageData) {
			this.logger.error(`Missing page data for page "${pageId}"`);
			return;
		}

		this.header?.updatePage(pageData);

		const $bg = this.$('#page-bg');
		const video = /** @type {HTMLVideoElement} */ ($bg.find('video').get(0));
		const currentVideoSrc = video.src;

		if (/\.mp4/u.test(pageBg)) {
			// Video background
			$bg.css({ backgroundImage: '' });
			if (pageBg !== currentVideoSrc) {
				video.src = pageBg;
				this.playVideo(video);
			}
		} else if (/\.(jpg|png)/.test(pageBg || arcadeBg || '')) {
			// Image background
			this.cleanUpVideo(video);
			$bg.css({ backgroundImage: `url("${pageBg || arcadeBg || ''}")` });
		} else {
			// Default arcade background
			this.cleanUpVideo(video);
		}

		const bgAudio = pageBgAudio || arcadeBgAudio;
		if (bgAudio) {
			AudioManager.playBackgroundMusic(bgAudio);
		}
	}

	/**
	 * Handles the page mounted event in the Arcade application.
	 * Updates help popup visibility based on the current page.
	 *
	 * @param {string} pageId - The ID of the current page.
	 * @returns {void}
	 */
	onPageMounted(pageId) {
		this.logger.log(`[${this.onPageMounted.name}]`, { pageId });

		const { pages } = this.arcadeState.attributes;
		const pageData = pages?.find(({ id }) => id === pageId);
		const { showInfoButton = false } = /** @type {PageOptions} */ ({ ...pageData });

		if (showInfoButton && !InfoButton.isEnabled) {
			InfoButton.enable();
		} else if (!showInfoButton && InfoButton.isEnabled) {
			InfoButton.disable();
		}
	}

	/**
	 * @param {string} pageId The id of the arcade page to access
	 * @returns {PageOptions | GenericButtonOptions | undefined}
	 */
	getPage(pageId) {
		this.logger.log(`[${this.getPage.name}]`, { pageId });

		return this.arcadeState?.get(ATTRIBUTES.pages)?.find(({ id = '' }) => id === pageId);
	}
}
